import { useEffect, useRef, useState, useCallback } from 'react';
import { endOfDay } from 'date-fns';

import { Messages } from 'primereact/messages';
import { TabView, TabPanel } from 'primereact/tabview';
import { ProgressSpinner } from 'primereact/progressspinner';

import ReportingService from '../services/reportingService';
import { AppPage } from '../components/general';
import { LineChartWidget, PieChartWidget } from '../components/widget';
import { ChartSeries, PieSeries } from '../models/chart/chart';
import { CreditSummary, FeesSummary, VoxSummary } from '../models/reporting/reporting';
import CalendarRange, { DefaultRangeSelectType } from '../components/common/CalendarRange';
import SummaryCard from '../components/dashboard/SummaryCard';
import VendorTable from '../components/dashboard/VendorTable';
import VoxVendorTable from '../components/dashboard/VoxVendorTable';
import DashboardDetailDialog, { DetailType } from '../components/dashboard/DashboardDetailDialog';

const TAB_FEES = 0;
const TAB_CREDIT = 1;
const TAB_VOX = 2;

const Dashboard = (): JSX.Element => {
  const errors = useRef<Messages>(null);

  const [activeTab, setActiveTab] = useState(TAB_FEES);

  const [creditLoading, setCreditLoading] = useState(false);
  const [feesLoading, setFeesLoading] = useState(true);
  const [voxLoading, setVoxLoading] = useState(false);

  const [credit, setCredit] = useState<CreditSummary | null>(null);
  const [fees, setFees] = useState<FeesSummary | null>(null);
  const [vox, setVox] = useState<VoxSummary | null>(null);

  const [fetchedTabs, setFetchedTabs] = useState<Set<number>>(new Set());

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [dateRange, setDateRange] = useState<Date[] | undefined>([start, end]);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailType, setDetailType] = useState<DetailType>('credit');
  const [detailVendor, setDetailVendor] = useState<string | undefined>();
  const [detailStatus, setDetailStatus] = useState<string | undefined>();

  const reportingService = ReportingService.getInstance();

  const showError = (error: string) => {
    errors.current?.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
  };

  const fetchFees = useCallback(
    (startDate: Date, endDate: Date) => {
      setFeesLoading(true);
      reportingService
        .getFeesSummary(startDate, endDate)
        .then((data) => setFees(data))
        .catch((err: Error) => showError(err.message))
        .finally(() => setFeesLoading(false));
    },
    [reportingService]
  );

  const fetchCredit = useCallback(
    (startDate: Date, endDate: Date) => {
      setCreditLoading(true);
      reportingService
        .getCreditSummary(startDate, endDate)
        .then((data) => setCredit(data))
        .catch((err: Error) => showError(err.message))
        .finally(() => setCreditLoading(false));
    },
    [reportingService]
  );

  const fetchVox = useCallback(
    (startDate: Date, endDate: Date) => {
      setVoxLoading(true);
      reportingService
        .getVoxSummary(startDate, endDate)
        .then((data) => setVox(data))
        .catch((err: Error) => showError(err.message))
        .finally(() => setVoxLoading(false));
    },
    [reportingService]
  );

  const fetchTab = useCallback(
    (tabIndex: number, startDate: Date, endDate: Date) => {
      if (tabIndex === TAB_FEES) fetchFees(startDate, endDate);
      else if (tabIndex === TAB_CREDIT) fetchCredit(startDate, endDate);
      else if (tabIndex === TAB_VOX) fetchVox(startDate, endDate);
      setFetchedTabs((prev) => new Set(prev).add(tabIndex));
    },
    [fetchFees, fetchCredit, fetchVox]
  );

  useEffect(() => {
    fetchTab(TAB_FEES, start, end);
  }, []);

  const onTabChange = (index: number) => {
    setActiveTab(index);
    const [s, e] = [dateRange?.[0] ?? start, dateRange?.[1] ?? end];
    if (!fetchedTabs.has(index)) {
      fetchTab(index, s, e);
    }
  };

  const openDetail = (type: DetailType, vendor?: string, status?: string) => {
    setDetailType(type);
    setDetailVendor(vendor);
    setDetailStatus(status);
    setDetailVisible(true);
  };

  const buildDailyChart = (
    dailyCounts: { date: Date; count: number; successCount: number; failedCount: number }[] | undefined
  ): ChartSeries[] | undefined => {
    if (!dailyCounts || dailyCounts.length === 0) return undefined;
    return [
      {
        name: 'Success',
        data: dailyCounts.map((d) => ({ x: new Date(d.date).getTime(), y: d.successCount })),
      },
      {
        name: 'Failed',
        data: dailyCounts.map((d) => ({ x: new Date(d.date).getTime(), y: d.failedCount })),
      },
    ];
  };

  const buildStatusPie = (
    statusBreakdown: { status: string; count: number }[] | undefined
  ): PieSeries | undefined => {
    if (!statusBreakdown || statusBreakdown.length === 0) return undefined;
    return {
      labels: statusBreakdown.map((s) => s.status),
      data: statusBreakdown.map((s) => s.count),
    };
  };

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${Math.round(ms)} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  };

  const renderSpinner = () => (
    <div className="flex justify-center items-center py-20">
      <ProgressSpinner style={{ width: '60px', height: '60px' }} />
    </div>
  );

  return (
    <AppPage
      title="Dashboard"
      breadcrumbs="Home - Dashboard"
      headerEnd={
        <CalendarRange
          rangeSelectOptions={[
            DefaultRangeSelectType.PastWeek,
            DefaultRangeSelectType.Past2Weeks,
            DefaultRangeSelectType.Past30Days,
            DefaultRangeSelectType.CurrentMonth,
          ]}
          value={dateRange}
          onValueChange={(value) => {
            if (!!value && value.length === 2) {
              fetchTab(activeTab, value[0], value[1]);
              setFetchedTabs(new Set([activeTab]));
            }
            setDateRange(value);
          }}
        />
      }
    >
      <Messages ref={errors} />

      <TabView activeIndex={activeTab} onTabChange={(e) => onTabChange(e.index)}>
        {/* ── Fees Tab ── */}
        <TabPanel header="Fees Requests" leftIcon="pi pi-dollar mr-2">
          {feesLoading ? renderSpinner() : (
            <div className="!grid gap-5">
              <div className="!grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <SummaryCard
                  title="Total Requests"
                  value={fees?.totalRequests?.toLocaleString() ?? '0'}
                  icon="pi pi-chart-bar"
                  bgClass="!bg-[#d1f0fd]"
                  onClick={() => openDetail('fees')}
                />
                <SummaryCard
                  title="Success"
                  value={fees?.successCount?.toLocaleString() ?? '0'}
                  icon="pi pi-check-circle"
                  bgClass="!bg-[#d4edda]"
                  onClick={() => openDetail('fees', undefined, 'Success')}
                />
                <SummaryCard
                  title="Failed"
                  value={fees?.failedCount?.toLocaleString() ?? '0'}
                  icon="pi pi-times-circle"
                  bgClass="!bg-[#f8d7da]"
                  onClick={() => openDetail('fees', undefined, 'Failed')}
                />
                <SummaryCard
                  title="Success Rate"
                  value={`${fees?.successRate ?? 0}%`}
                  icon="pi pi-percentage"
                  bgClass="!bg-[#eee2ff]"
                />
                <SummaryCard
                  title="Avg Duration"
                  value={formatDuration(fees?.avgDurationMs ?? 0)}
                  subtitle={`${fees?.dailyAverage ?? 0} / day`}
                  icon="pi pi-clock"
                />
              </div>

              <div className="!grid grid-cols-1 lg:grid-cols-2 gap-5">
                <LineChartWidget title="Daily Fees Requests" chartSeries={buildDailyChart(fees?.dailyCounts)} />
                <PieChartWidget title="Status Breakdown" pieSeries={buildStatusPie(fees?.statusBreakdown)} />
              </div>

              <VendorTable
                title="Fees Vendors"
                vendors={fees?.vendorBreakdown ?? []}
                onVendorClick={(vendor) => openDetail('fees', vendor)}
              />
            </div>
          )}
        </TabPanel>

        {/* ── Credit Tab ── */}
        <TabPanel header="Credit Requests" leftIcon="pi pi-credit-card mr-2">
          {creditLoading ? renderSpinner() : (
            <div className="!grid gap-5">
              <div className="!grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <SummaryCard
                  title="Total Requests"
                  value={credit?.totalRequests?.toLocaleString() ?? '0'}
                  icon="pi pi-chart-bar"
                  bgClass="!bg-[#d1f0fd]"
                  onClick={() => openDetail('credit')}
                />
                <SummaryCard
                  title="Success"
                  value={credit?.successCount?.toLocaleString() ?? '0'}
                  icon="pi pi-check-circle"
                  bgClass="!bg-[#d4edda]"
                  onClick={() => openDetail('credit', undefined, 'Success')}
                />
                <SummaryCard
                  title="Failed"
                  value={credit?.failedCount?.toLocaleString() ?? '0'}
                  icon="pi pi-times-circle"
                  bgClass="!bg-[#f8d7da]"
                  onClick={() => openDetail('credit', undefined, 'Failed')}
                />
                <SummaryCard
                  title="Success Rate"
                  value={`${credit?.successRate ?? 0}%`}
                  icon="pi pi-percentage"
                  bgClass="!bg-[#eee2ff]"
                />
                <SummaryCard
                  title="Avg Duration"
                  value={formatDuration(credit?.avgDurationMs ?? 0)}
                  subtitle={`${credit?.dailyAverage ?? 0} / day`}
                  icon="pi pi-clock"
                />
              </div>

              <div className="!grid grid-cols-1 lg:grid-cols-2 gap-5">
                <LineChartWidget title="Daily Credit Requests" chartSeries={buildDailyChart(credit?.dailyCounts)} />
                <PieChartWidget title="Status Breakdown" pieSeries={buildStatusPie(credit?.statusBreakdown)} />
              </div>

              <VendorTable
                title="Credit Vendors"
                vendors={credit?.vendorBreakdown ?? []}
                onVendorClick={(vendor) => openDetail('credit', vendor)}
              />
            </div>
          )}
        </TabPanel>

        {/* ── Vox Tab ── */}
        <TabPanel header="Vox Orders" leftIcon="pi pi-bolt mr-2">
          {voxLoading ? renderSpinner() : (
            <div className="!grid gap-5">
              <div className="!grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <SummaryCard
                  title="Total Orders"
                  value={vox?.totalOrders?.toLocaleString() ?? '0'}
                  icon="pi pi-chart-bar"
                  bgClass="!bg-[#d1f0fd]"
                  onClick={() => openDetail('vox')}
                />
                <SummaryCard
                  title="Success"
                  value={vox?.successCount?.toLocaleString() ?? '0'}
                  icon="pi pi-check-circle"
                  bgClass="!bg-[#d4edda]"
                  onClick={() => openDetail('vox', undefined, 'Success')}
                />
                <SummaryCard
                  title="Failed"
                  value={vox?.failedCount?.toLocaleString() ?? '0'}
                  icon="pi pi-times-circle"
                  bgClass="!bg-[#f8d7da]"
                  onClick={() => openDetail('vox', undefined, 'Failed')}
                />
                <SummaryCard
                  title="Pending"
                  value={vox?.pendingCount?.toLocaleString() ?? '0'}
                  icon="pi pi-hourglass"
                  bgClass="!bg-[#fff3cd]"
                  onClick={() => openDetail('vox', undefined, 'Pending')}
                />
                <SummaryCard
                  title="Success Rate"
                  value={`${vox?.successRate ?? 0}%`}
                  subtitle={`${vox?.dailyAverage ?? 0} / day`}
                  icon="pi pi-percentage"
                  bgClass="!bg-[#eee2ff]"
                />
              </div>

              <div className="!grid grid-cols-1 lg:grid-cols-2 gap-5">
                <LineChartWidget title="Daily Vox Orders" chartSeries={buildDailyChart(vox?.dailyCounts)} />
                <PieChartWidget title="Status Breakdown" pieSeries={buildStatusPie(vox?.statusBreakdown)} />
              </div>

              <VoxVendorTable
                title="Vox Vendors"
                vendors={vox?.vendorBreakdown ?? []}
                onVendorClick={(vendorId) => openDetail('vox', String(vendorId))}
              />
            </div>
          )}
        </TabPanel>
      </TabView>

      <DashboardDetailDialog
        visible={detailVisible}
        onHide={() => setDetailVisible(false)}
        type={detailType}
        startDate={dateRange?.[0] ?? start}
        endDate={dateRange?.[1] ?? end}
        vendor={detailVendor}
        status={detailStatus}
      />
    </AppPage>
  );
};

export default Dashboard;
